import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { UtilitiesModule } from '../utilities/utilities.module';
import { FormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';

import { UserLockedInformationComponent } from './user-locked-information/user-locked-information.component';
import { ComponentsModule } from '../components/components.module';
import { RequestAccessComponent } from './request-access/request-access.component';
import { UserService } from './user.service';
import { UserSummarySettingsComponent } from './user-summary-settings/user-summary-settings.component';
import { FiProfileViewModule } from '../components/profile-view/profile-view.module';
import { FiChangePasswordTooltipModule } from '../components/change-password-tooltip/change-password-tooltip.module';
import { UserListContainerComponent } from './user-list-container/user-list-container.component';
import { FiTextItemLinksToPopupOpeners } from '../utilities/text-item-links-to-popup/text-item-links-to-popup.directive';
import { DataFieldEditModule } from '../data-field/data-field-edit/data-field-edit.module';
import { FiUserEditDataFieldNameComponent } from './user-edit/data-field/name.component';
import { FiDataFieldViewModule } from '@fi-app/data-field/data-field-view/data-field-view.module';
import { FiLinkToType } from '@fi-shared/fi-text-item-helper/fi-link-to-type';
import { FiCaptchaModule } from '@fi-app/utilities/captcha/fi-captcha.module';
import { UserAccessRequestsService } from '@fi-app/user/user-access-requests.service';
import { FiPageHeaderButtonGroupModule } from '@fi-shared/fi-page-header-button-group/fi-page-header-button-group.module';
import { UserListProfileIconColumnComponent } from './user-list/user-list-profile-icon-column/user-list-profile-icon-column.component';
import { FiProfileIconModule } from '@fi-shared/profile-icon/profile-icon.module';
import { FiItemsListModule } from '@fi-shared/fi-items-list/fi-items-list.module';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { FiPageHeaderModule } from '@fi-app/components/fi-page-header/fi-page-header.module';
import { MatCardModule } from '@angular/material/card';
import { SelectSearchModule } from '@fi-shared/fi-select-search/select-search.module';
import { FiErrorMessageModule } from '@fi-app/shared/fi-error-message/fi-error-message.module';
import { FiUserEditProfileInformationComponent } from './user-edit/user-edit-new-layout/fi-user-edit-profile-information/fi-user-edit-profile-information.component';
import { FiUserEditStatusComponent } from './user-edit/user-edit-new-layout/fi-user-edit-status/fi-user-edit-status.component';
import { FiUserEditContactTabComponent } from './user-edit/user-edit-new-layout/fi-user-edit-contact-tab/fi-user-edit-contact-tab.component';
import { FiUserEditWorkProfileTabComponent } from './user-edit/user-edit-new-layout/fi-user-edit-work-profile-tab/fi-user-edit-work-profile-tab.component';
import { MatTabsModule } from '@angular/material/tabs';
import { MatDividerModule } from '@angular/material/divider';
import { UserEditNewLayoutComponent } from './user-edit/user-edit-new-layout/user-edit-new-layout.component';
import { FiUserViewDialogComponent } from './fi-user-view-dialog/fi-user-view-dialog.component';
import { FiUserViewProfileTabGroupComponent } from './fi-user-view-dialog/fi-user-view-profile-tab-group/fi-user-view-profile-tab-group.component';
import { FiUserViewProfileWorkProfileTabComponent } from './fi-user-view-dialog/fi-user-view-profile-tab-group/fi-user-view-profile-work-profile-tab/fi-user-view-profile-work-profile-tab.component';
import { FiUserViewProfileContactTabComponent } from './fi-user-view-dialog/fi-user-view-profile-tab-group/fi-user-view-profile-contact-tab/fi-user-view-profile-contact-tab.component';
import { FiUserViewProfileOtherTabComponent } from './fi-user-view-dialog/fi-user-view-profile-tab-group/fi-user-view-profile-other-tab/fi-user-view-profile-other-tab.component';
import { FiUserEditOtherTabComponent } from './user-edit/user-edit-new-layout/fi-user-edit-other-tab/fi-user-edit-other-tab.component';
import { FiUserEditSettingTabComponent } from './user-edit/user-edit-new-layout/fi-user-edit-setting-tab/fi-user-edit-setting-tab.component';
import { FiUserEditSecurityTabComponent } from './user-edit/user-edit-new-layout/fi-user-edit-security-tab/fi-user-edit-security-tab.component';
import { FiCustomValidatorsModule } from '@fi-shared/fi-directives/fi-custom-validators/fi-custom-validators.module';
import { MatRadioModule } from '@angular/material/radio';
import { FiUserListComponent } from '@fi-app/user/user-list/fi-user-list/fi-user-list.component';
import { FiLocalSearch2Module } from '@fi-shared/fi-local-search2/fi-local-search2.module';
import { FiWrapperTableModule } from '@fi-shared/fi-wrapper-table/fi-wrapper-table.module';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { FiUserListTableComponent } from '@fi-app/user/user-list/fi-user-list/fi-user-list-table/fi-user-list-table.component';
import { FiUserListContainerDialogComponent } from './fi-user-list-container-dialog/fi-user-list-container-dialog.component';
import { FiUserListExtendedSearchComponent } from './user-list/fi-user-list/fi-user-list-extended-search/fi-user-list-extended-search.component';
import { FiUserSignInTypeComponent } from '@fi-app/user/user-edit/user-sign-in-type/fi-user-sign-in-type.component';
import { UserEditorPageHeaderComponent } from '@fi-app/user/user-edit/user-edit-new-layout/user-editor-page-header/user-editor-page-header.component';
import { FiContactsModule } from '@fi-shared/fi-contacts/fi-contacts.module';

const FiTextItemLinksToPopupOpenersUserProfileProvider = {
    provide: FiTextItemLinksToPopupOpeners,
    useFactory: (userService: UserService) => (linkToModel) => {
        if (linkToModel.Type === FiLinkToType.UserProfile) {
            userService.openUserViewDialog(linkToModel.Value.Id);
            return true;
        }
    },
    deps: [UserService],
    multi: true,
};

@NgModule({
    imports: [
        CommonModule,
        UtilitiesModule,
        FormsModule,
        ComponentsModule,
        MatCheckboxModule,
        MatFormFieldModule,
        FiProfileViewModule,
        FiChangePasswordTooltipModule,
        DataFieldEditModule,
        FiDataFieldViewModule,
        FiCaptchaModule,
        FiPageHeaderButtonGroupModule,
        FiProfileIconModule,
        FiItemsListModule,
        MatIconModule,
        MatButtonModule,
        MatInputModule,
        FiPageHeaderModule,
        MatCardModule,
        SelectSearchModule,
        FiErrorMessageModule,
        MatTabsModule,
        MatDividerModule,
        FiCustomValidatorsModule,
        MatRadioModule,
        FiLocalSearch2Module,
        FiWrapperTableModule,
        MatSortModule,
        MatTableModule,
        FiContactsModule,
    ],
    declarations: [
        UserLockedInformationComponent,
        RequestAccessComponent,
        UserSummarySettingsComponent,
        UserListContainerComponent,
        FiUserEditDataFieldNameComponent,
        UserListProfileIconColumnComponent,
        FiUserEditProfileInformationComponent,
        FiUserEditStatusComponent,
        FiUserEditContactTabComponent,
        FiUserEditWorkProfileTabComponent,
        UserEditNewLayoutComponent,
        FiUserViewDialogComponent,
        FiUserViewProfileTabGroupComponent,
        FiUserViewProfileWorkProfileTabComponent,
        FiUserViewProfileContactTabComponent,
        FiUserViewProfileOtherTabComponent,
        FiUserEditOtherTabComponent,
        FiUserEditSettingTabComponent,
        FiUserEditSecurityTabComponent,
        FiUserListComponent,
        FiUserListTableComponent,
        FiUserListContainerDialogComponent,
        FiUserListExtendedSearchComponent,
        FiUserSignInTypeComponent,
        UserEditorPageHeaderComponent,
    ],
    providers: [UserService, UserAccessRequestsService, FiTextItemLinksToPopupOpenersUserProfileProvider],
    exports: [UserLockedInformationComponent, FiUserListComponent, FiUserListTableComponent],
})
export class UserModule {}
